import { Alert, ExternalLink, Skeleton, Table, Text } from '@ui/themed';
import { t } from 'i18next';
import { PageSection } from '../../../components/page-section';
import { useBatchFetchStock } from '../../stock';
import { DOCS_URLS, I18N_COMPONENTS } from '../constants';
import type { Variant } from '../types/product';
import { StockLocationsUpsell } from './stock-locations-upsell';

export function ProductStockCard({ variant }: { variant: Variant }) {
  const sku = variant.identifiers.sku;
  const tracked = variant.stock_management === 'sparklayer';

  const { data, isLoading } = useBatchFetchStock(tracked ? [sku] : undefined, {
    allStockLocations: true,
  });

  const stockLevels = data?.[0]?.stock_levels ?? [];
  const headings = [
    t(`${I18N_COMPONENTS}.stock_card.headings.location`),
    t(`${I18N_COMPONENTS}.stock_card.headings.stock_level`),
    t(`${I18N_COMPONENTS}.stock_card.headings.min_stock_level`),
    t(`${I18N_COMPONENTS}.stock_card.headings.restock_date`),
    t(`${I18N_COMPONENTS}.stock_card.headings.last_updated`),
  ];

  return (
    <PageSection title={t(`${I18N_COMPONENTS}.stock_card.title`)}>
      {!tracked ? (
        <Alert headingLevel="h3">
          <span className="flex flex-wrap items-center gap-x-1">
            {t(`${I18N_COMPONENTS}.stock_card.not_tracked`)}
            <ExternalLink link={DOCS_URLS.stockDisplay} showIcon>
              {t('links:learn_more')}
            </ExternalLink>
          </span>
        </Alert>
      ) : isLoading ? (
        <Skeleton />
      ) : stockLevels.length === 0 ? (
        <Alert variant="warning" headingLevel="h3">
          <span className="flex flex-wrap items-center gap-x-1">
            {t(`${I18N_COMPONENTS}.stock_card.no_data`)}
            <ExternalLink link={DOCS_URLS.stockDisplay} showIcon>
              {t('links:learn_more')}
            </ExternalLink>
          </span>
        </Alert>
      ) : (
        <Table headings={headings} caption={t(`${I18N_COMPONENTS}.stock_card.title`)}>
          {stockLevels.map((level, index) => (
            <Table.Row key={level.stock_location_id} id={level.stock_location_id} index={index}>
              <Table.Cell>
                <Text variant="secondary">{level.stock_location_name}</Text>
              </Table.Cell>
              <Table.Cell>
                <Text variant="secondary">{level.stock_level}</Text>
              </Table.Cell>
              <Table.Cell>
                <Text variant="secondary">{level.min_stock_level}</Text>
              </Table.Cell>
              <Table.Cell>
                <Text variant="secondary">{level.restock_date ?? t('labels:none')}</Text>
              </Table.Cell>
              <Table.Cell>
                <Text variant="secondary">{formatDateTime(level.updated_at)}</Text>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table>
      )}

      <StockLocationsUpsell />
    </PageSection>
  );
}

function formatDateTime(date: string | null | undefined): string {
  if (!date) {
    return t('labels:none');
  }
  return new Intl.DateTimeFormat(undefined, {
    year: '2-digit',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  }).format(new Date(date));
}
